/**
 * LoadingIndicator Component
 *
 * Global loading indicator that displays in the header when
 * async operations are in progress. Uses the LoadingProvider
 * context for state management.
 *
 * Also exports unified Spinner component for consistent loading states.
 *
 * @example
 * // In App.js or layout component
 * <LoadingProvider>
 *   <GlobalLoadingIndicator />
 *   <App />
 * </LoadingProvider>
 *
 * @example
 * // Inline spinner
 * import { Spinner } from './LoadingIndicator';
 * <Spinner size="sm" />
 *
 * @module components/ui/LoadingIndicator
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useLoadingState } from '../../hooks/useLoadingState';

/**
 * GlobalLoadingIndicator Component
 *
 * A subtle animated bar at the top of the page showing global loading state.
 * Only renders when at least one loading operation is active.
 */
export const GlobalLoadingIndicator = () => {
  const { isLoading } = useLoadingState();

  if (!isLoading) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 h-1 z-50 bg-black/20 overflow-hidden"
      role="progressbar"
      aria-label="Loading in progress"
      aria-busy="true"
    >
      <div
        className="h-full bg-gradient-to-r from-primary-500 via-primary-400 to-primary-500 animate-loading-bar"
        style={{
          animation: 'loading-bar 2s ease-in-out infinite',
        }}
      />
    </div>
  );
};

/**
 * LoadingBar Component
 *
 * Standalone loading bar with customizable appearance.
 * Does not use global loading context.
 *
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether to show the loading bar
 * @param {string} props.color - Tailwind color class for the bar
 * @param {number} props.height - Height in pixels
 */
export const LoadingBar = ({
  isLoading = false,
  color = 'bg-primary-500',
  height = 2,
}) => {
  if (!isLoading) return null;

  return (
    <div
      className="w-full overflow-hidden bg-black/20"
      style={{ height }}
      role="progressbar"
      aria-busy="true"
    >
      <div
        className={`h-full ${color}`}
        style={{
          animation: 'loading-bar 2s ease-in-out infinite',
        }}
      />
    </div>
  );
};

LoadingBar.propTypes = {
  isLoading: PropTypes.bool,
  color: PropTypes.string,
  height: PropTypes.number,
};

/**
 * SpinnerIndicator Component
 *
 * Circular spinner for inline loading states.
 *
 * @param {Object} props
 * @param {string} props.size - Size: 'small', 'medium', 'large'
 * @param {string} props.color - Tailwind color class
 * @param {string} props.className - Additional classes
 */
export const SpinnerIndicator = ({
  size = 'medium',
  color = 'text-primary-500',
  className = '',
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]} ${color} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

SpinnerIndicator.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  className: PropTypes.string,
};

/**
 * Spinner Component - Unified spinner for consistent loading states
 *
 * This is the single source of truth for spinners across the application.
 * Use this component instead of creating inline spinners.
 *
 * @param {Object} props
 * @param {string} props.size - Size: 'sm' (16px), 'md' (20px), 'lg' (24px)
 * @param {string} props.color - Tailwind color class (default: current color)
 * @param {string} props.className - Additional classes
 */
export const Spinner = ({
  size = 'md',
  color = 'currentColor',
  className = '',
}) => {
  const sizeMap = {
    sm: 'w-4 h-4',    // 16px
    md: 'w-5 h-5',    // 20px
    lg: 'w-6 h-6',    // 24px
  };

  const sizeClass = sizeMap[size] || sizeMap.md;

  return (
    <svg
      className={`animate-spin ${sizeClass} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading"
      style={{ color: color === 'currentColor' ? undefined : color }}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.string,
  className: PropTypes.string,
};

/**
 * LoadingOverlay Component
 *
 * Full-page or container overlay with spinner.
 *
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether to show overlay
 * @param {string} props.message - Optional loading message
 * @param {boolean} props.fullScreen - Whether to cover full screen
 */
export const LoadingOverlay = ({
  isLoading = false,
  message = '',
  fullScreen = false,
}) => {
  if (!isLoading) return null;

  return (
    <div
      className={`${
        fullScreen ? 'fixed inset-0' : 'absolute inset-0'
      } bg-black/50 backdrop-blur-sm flex items-center justify-center z-40`}
      role="alert"
      aria-busy="true"
    >
      <div className="flex flex-col items-center space-y-4">
        <SpinnerIndicator size="large" />
        {message && (
          <p className="text-white text-sm font-medium">{message}</p>
        )}
      </div>
    </div>
  );
};

LoadingOverlay.propTypes = {
  isLoading: PropTypes.bool,
  message: PropTypes.string,
  fullScreen: PropTypes.bool,
};

/**
 * PulsingDot Component
 *
 * Simple pulsing dot indicator for compact spaces.
 *
 * @param {Object} props
 * @param {string} props.color - Tailwind color class
 */
export const PulsingDot = ({ color = 'bg-primary-500' }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${color} animate-pulse`}
    aria-label="Loading"
    role="status"
  />
);

PulsingDot.propTypes = {
  color: PropTypes.string,
};

export default GlobalLoadingIndicator;
